import { useState, useRef, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useCategory } from '../../../CategoryContext';
import { Context } from '../../../AuthContext';
import AlertDialog from '../../../components/AlertDialog';

// Slack's consumer workspace-creation page. This lives entirely on slack.com and
// gives our app nothing back, so after the user finishes here we hand off to OAuth.
const CREATE_WORKSPACE_URL = 'https://slack.com/get-started#/createnew';

const headingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '24px',
  color: '#1A2238',
};

const bodySx = { color: 'black', fontFamily: 'Inter, sans-serif' };

// `slack` (connection/activity) and `onRefresh` are owned by Workbench so the
// tab-bar activity dot stays in sync with this tab's content.
const Slack = ({ verticalId, slack, onRefresh }) => {
  const { isAdvancedEditor } = useCategory();
  const { user } = useContext(Context);
  const [searchParams, setSearchParams] = useSearchParams();

  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [banner, setBanner] = useState(null); // { kind: 'success' | 'error', text }

  // After the user goes through Slack's create flow, we surface the "finish connecting"
  // (OAuth) step instead of leaving them to hunt for the second button.
  const [awaitingConnect, setAwaitingConnect] = useState(false);

  const pollRef = useRef(null);

  const { loading, connected, workspace, hasNewActivity } = slack;

  // Surface the result of the OAuth round-trip (?slack=connected|error) and clean it up.
  useEffect(() => {
    const result = searchParams.get('slack');
    if (!result) return;
    if (result === 'connected') {
      setBanner({ kind: 'success', text: 'Slack workspace connected.' });
      onRefresh?.();
    } else if (result === 'error') {
      const message = searchParams.get('message');
      setBanner({
        kind: 'error',
        text: `Could not connect Slack${message ? `: ${message}` : ''}.`,
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('slack');
    next.delete('message');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, onRefresh]);

  // Stop polling the create-workspace popup if the user navigates away.
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Kicks off the Slack "Add to Slack" OAuth authorization via the backend, which
  // builds the signed-state URL. The browser is redirected to Slack's consent screen.
  const startSlackOAuth = async () => {
    if (!user?.uid || !verticalId) return;
    setConnecting(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}oauth/slack/start?vertical_id=${encodeURIComponent(
          verticalId
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setBanner({ kind: 'error', text: 'Could not start Slack connection.' });
        setConnecting(false);
      }
    } catch (e) {
      setBanner({ kind: 'error', text: 'Could not start Slack connection.' });
      setConnecting(false);
    }
  };

  const handleCreateWorkspace = () => {
    // Open Slack's creation flow in a popup so we can detect when it closes and
    // immediately offer the OAuth connect step. Browsers only honor "popup" when given
    // an explicit size AND position, so we center it on the current screen.
    const width = 1000;
    const height = 800;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
    const features = `popup=yes,width=${width},height=${height},left=${left},top=${top}`;
    const popup = window.open(CREATE_WORKSPACE_URL, 'slack-create-workspace', features);

    // Popup blocked → fall back to a normal tab and surface the connect step now.
    if (!popup) {
      window.open(CREATE_WORKSPACE_URL, '_blank', 'noopener,noreferrer');
      setAwaitingConnect(true);
      return;
    }

    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setAwaitingConnect(true);
      }
    }, 800);
  };

  // Clear the new-activity flag once the user actually opens the workspace.
  const markViewed = async () => {
    if (!user?.uid || !verticalId) return;
    try {
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}oauth/slack/mark-viewed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ vertical_id: verticalId }),
      });
    } catch (e) {
      // Non-fatal — the dot will simply clear on the next status refresh.
    }
    onRefresh?.();
  };

  const handleOpenWorkspace = () => {
    const webUrl = workspace?.url;
    const teamId = workspace?.teamId;

    if (!teamId) {
      // Without a team id we can't build the desktop deep link — just open the web app.
      if (webUrl) window.open(webUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Try the native Slack desktop app first via its deep link. If the app is
      // installed it takes focus (the tab blurs / becomes hidden), which cancels the
      // web fallback; otherwise nothing happens and we open the workspace in the browser.
      const appUrl = `slack://open?team=${teamId}`;
      let switchedToApp = false;

      const onVisibilityChange = () => {
        if (document.hidden) switchedToApp = true;
      };
      const onBlur = () => {
        switchedToApp = true;
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('blur', onBlur);

      window.location.href = appUrl;

      setTimeout(() => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('blur', onBlur);
        if (!switchedToApp && webUrl) {
          window.open(webUrl, '_blank', 'noopener,noreferrer');
        }
      }, 2000);
    }

    markViewed();
  };

  // Owner / Advanced Editor only — frees the vertical so a different workspace can be linked.
  const handleDisconnect = async () => {
    if (!user?.uid || !verticalId) return;
    setDisconnecting(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}oauth/slack/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ vertical_id: verticalId }),
      });
      setBanner(
        res.ok
          ? { kind: 'success', text: 'Slack workspace disconnected.' }
          : { kind: 'error', text: 'Could not disconnect the Slack workspace.' }
      );
    } catch (e) {
      setBanner({ kind: 'error', text: 'Could not disconnect the Slack workspace.' });
    } finally {
      setDisconnecting(false);
      onRefresh?.();
    }
  };

  const renderBanner = () =>
    banner && (
      <Typography
        sx={{
          mt: 2,
          fontFamily: 'Inter, sans-serif',
          color: banner.kind === 'success' ? '#1B7F4B' : '#C0392B',
        }}
      >
        {banner.text}
      </Typography>
    );

  const renderScenario1 = () => {
    // After creation, prompt the user to finish connecting the new workspace via OAuth.
    if (awaitingConnect) {
      return (
        <>
          <Typography sx={{ ...bodySx, mt: 2 }}>
            Once you’ve finished creating your workspace on Slack, connect it here to link it to
            this Vertical. You’ll be asked to approve access on Slack.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 3 }}>
            <Button
              variant="contained"
              onClick={startSlackOAuth}
              disabled={connecting}
              sx={{ textTransform: 'none', width: 260 }}
            >
              Finish Connecting Workspace
            </Button>
            <Button
              variant="text"
              onClick={() => setAwaitingConnect(false)}
              sx={{ textTransform: 'none', mt: 1, px: 0 }}
            >
              Start over
            </Button>
          </Box>
        </>
      );
    }

    return (
      <>
        <Typography sx={{ ...bodySx, mt: 2 }}>
          This Vertical does not have a Slack workspace connected yet. Create a new Slack workspace
          for this Vertical, or connect an existing one.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleCreateWorkspace}
            sx={{ textTransform: 'none', width: 300 }}
          >
            Create New Slack Workspace
          </Button>
          <div style={{ width: 300, display: 'flex', margin: '12px 0' }}>
            <Typography
              sx={{
                fontWeight: 600,
                margin: 'auto',
              }}
            >
              OR
            </Typography>
          </div>
          <Button
            variant="contained"
            onClick={startSlackOAuth}
            disabled={connecting}
            sx={{ textTransform: 'none', width: 300 }}
          >
            Connect Existing Slack Workspace
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ textAlign: 'left', py: 1, fontFamily: 'Inter, sans-serif' }}>
      <Typography sx={headingSx}>Slack Workspace Name</Typography>

      <Typography sx={{ ...bodySx, fontStyle: 'italic', mt: 0.5 }}>
        {connected ? workspace?.name : 'None Connected'}
      </Typography>

      {renderBanner()}

      {loading ? (
        <Typography sx={{ ...bodySx, mt: 2 }}>Loading…</Typography>
      ) : connected ? (
        /* Scenarios 3 & 4: a workspace is connected. Visible to everyone in the vertical. */
        <>
          <Typography sx={{ ...headingSx, mt: 4 }}>Recent Slack Activity</Typography>
          <Typography sx={{ ...bodySx, mt: 1 }}>
            {hasNewActivity ? 'Yes - new activity detected.' : 'No new activity detected.'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1.5,
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              onClick={handleOpenWorkspace}
              disabled={!workspace?.url && !workspace?.teamId}
              sx={{ textTransform: 'none' }}
            >
              Open Slack Workspace
            </Button>
            {isAdvancedEditor && (
              <AlertDialog
                dialogTitle="Disconnect Slack workspace?"
                dialogContentText="This unlinks the Slack workspace from this Vertical. Members will no longer be able to open it from here until a workspace is connected again. You can connect a different workspace afterwards."
                action={handleDisconnect}
              >
                <Button
                  variant="outlined"
                  color="error"
                  disabled={disconnecting}
                  sx={{ textTransform: 'none' }}
                >
                  Disconnect Workspace
                </Button>
              </AlertDialog>
            )}
          </Box>
        </>
      ) : isAdvancedEditor ? (
        /* Scenario 1: not connected, current user is the Vertical Owner or Advanced Editor. */
        renderScenario1()
      ) : (
        /* Scenario 2: not connected, current user is NOT the Owner or Advanced Editor. */
        <Typography sx={{ ...bodySx, mt: 2 }}>
          Slack has not been connected to this Vertical yet. Only the Vertical Owner or an Advanced
          Editor can create or assign the Slack workspace. Once connected, all invited users will be
          able to open it from here.
        </Typography>
      )}
    </Box>
  );
};

export default Slack;
